import { fetchReturnLabelFile, type ReturnRecord } from "@/features/resi/api";
import { giornoLungo, type Prenotazione } from "@/features/ritiri/api";
import { uploadThreadAttachments, type UploadedAttachment } from "./api";
import { canaleAccettaAllegati } from "./messageSide";

/**
 * Testo bozza per un RESO creato dalla chat (approvato da Davide). NON auto-inviato:
 * l'operatore controlla e invia. Per la SOSTITUZIONE il composer resta vuoto: il
 * tracking al cliente lo propone il dialog «creato ✓» (``AvvisaClienteTracking``).
 */
export const RESO_MESSAGE_TEMPLATE =
  "Buongiorno, come concordato in allegato trova l'etichetta di spedizione. " +
  "La preghiamo di: applicare l'etichetta su ogni scatola; imballare la merce come " +
  "le è arrivata (se non possibile, la protegga al meglio per evitare danni nel " +
  "trasporto); consegnare il pacco al corriere. Restiamo a disposizione. Un cordiale saluto.";

/** Col ritiro prenotato il cliente non porta niente: aspetta il corriere. */
export function testoResoConRitiro(ritiro: Pick<Prenotazione, "data_ritiro" | "codice">): string {
  return (
    "Buongiorno, come concordato in allegato trova l'etichetta di spedizione. " +
    "La preghiamo di: applicare l'etichetta su ogni scatola; imballare la merce come " +
    "le è arrivata (se non possibile, la protegga al meglio per evitare danni nel " +
    `trasporto); tenere il pacco pronto: il corriere SDA passerà a ritirarlo ${giornoLungo(ritiro.data_ritiro)}` +
    `${ritiro.codice ? ` (prenotazione ${ritiro.codice})` : ""}. Restiamo a disposizione. Un cordiale saluto.`
  );
}

/** Bozza reso in attesa nel composer, LEGATA al thread in cui è nata: senza
 *  ``threadId`` la bozza è un record nudo e ChatDetail non può sapere se
 *  appartiene alla conversazione che ha a schermo (vedi la guardia nell'effect
 *  di ``MessaggiPage``). Nome diverso da ``ResoDraft``, che è tutt'altro (l'esito
 *  della preparazione: allegati caricati + testo). ``ritiro``: il ritiro dal
 *  cliente prenotato insieme al reso, se c'è. Vive qui e non in MessaggiPage,
 *  che è oltre il tetto di righe e può solo scendere. */
export type BozzaReso = { record: ReturnRecord; threadId: number; ritiro: Prenotazione | null };

export interface ResoDraft {
  attachments: UploadedAttachment[];
  message: string;
}

/**
 * Prepara la bozza di risposta per un reso creato dalla chat: scarica l'etichetta
 * PDF e la allega al thread; SOLO se l'upload riesce ritorna il testo template.
 *
 * ⚠️ L'ordine è voluto: prima l'allegato, poi il testo. Se l'upload fallisce la
 * funzione solleva (il chiamante NON deve mettere il testo — prometterebbe un
 * allegato assente); il PDF resta comunque scaricabile dallo stato «creato ✓».
 */
export async function prepareResoDraft(
  threadId: number,
  record: Pick<ReturnRecord, "id" | "order_reference" | "kind">,
  ritiro?: Prenotazione | null,
  source?: string,
): Promise<ResoDraft> {
  // Amazon e ManoMano non spediscono allegati: niente upload né testo «in allegato».
  if (source && !canaleAccettaAllegati(source)) {
    throw new Error(
      "Su questo canale non si possono mandare allegati: scarica l'etichetta dal reso e mandala in altro modo.",
    );
  }
  const file = await fetchReturnLabelFile(record.id, record.order_reference, record.kind);
  const attachments = await uploadThreadAttachments(threadId, [file]);
  // Il giorno lo si promette al cliente solo se SDA l'ha davvero registrato.
  const message =
    ritiro?.stato === "prenotato" ? testoResoConRitiro(ritiro) : RESO_MESSAGE_TEMPLATE;
  return { attachments, message };
}
